<script setup>
/**
 * BulkPriorityModal — Fase 4.2 frontend
 *
 * Set priority on selected cards in one go. Reuses BulkStageFilter
 * (Fase 4.3) so the operator can narrow the operation to a single stage.
 *
 * Backend: POST /api/v1/accounts/:id/pipeline/bulk_actions/set_priority
 * (PipelineCardsAPI.bulkSetPriority).
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from '../../../../components-next/button/Button.vue';
import BulkStageFilter from './BulkStageFilter.vue';

const props = defineProps({
  items: { type: Array, required: true },
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const store = useStore();

const PRIORITY_OPTIONS = ['none', 'low', 'medium', 'high', 'urgent'];

const selectedPriority = ref('medium');
const stageFilter = ref('all');
const selectedIds = ref([]);

const stages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.stages) return [];
  return Object.entries(pipeline.stages)
    .map(([id, stage]) => ({ id, name: stage.name, position: stage.position }))
    .sort((a, b) => a.position - b.position);
});

const filteredItems = computed(() => {
  if (stageFilter.value === 'all') return props.items;
  return props.items.filter(
    item => (item.pipeline_stage || item.stage) === stageFilter.value
  );
});

const selectedVisibleIds = computed(() => {
  const visibleIds = new Set(filteredItems.value.map(item => item.id));
  return selectedIds.value.filter(id => visibleIds.has(id));
});

const isAllSelected = computed(() => {
  return (
    filteredItems.value.length > 0 &&
    selectedVisibleIds.value.length === filteredItems.value.length
  );
});

const toggleSelectAll = () => {
  const visibleIds = filteredItems.value.map(item => item.id);
  const allVisibleSelected = visibleIds.every(id =>
    selectedIds.value.includes(id)
  );
  if (allVisibleSelected) {
    selectedIds.value = selectedIds.value.filter(
      id => !visibleIds.includes(id)
    );
  } else {
    selectedIds.value = Array.from(
      new Set([...selectedIds.value, ...visibleIds])
    );
  }
};

const toggleItem = itemId => {
  const index = selectedIds.value.indexOf(itemId);
  if (index === -1) {
    selectedIds.value.push(itemId);
  } else {
    selectedIds.value.splice(index, 1);
  }
};

const priorityClass = priority => {
  const map = {
    urgent: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-9',
    high: 'bg-orange-3 text-orange-11 border-orange-9',
    medium: 'bg-n-amber-3 text-n-amber-11 border-n-amber-9',
    low: 'bg-blue-3 text-blue-11 border-blue-9',
    none: 'bg-n-alpha-2 text-n-slate-10 border-n-weak',
  };
  return map[priority] || map.none;
};

const handleConfirm = () => {
  if (props.isSubmitting || selectedVisibleIds.value.length === 0) return;

  emit('confirm', {
    cardIds: selectedVisibleIds.value,
    pipelineStage: stageFilter.value === 'all' ? null : stageFilter.value,
    priority: selectedPriority.value,
  });
};
</script>

<template>
  <div class="w-full max-w-[560px]">
    <div class="p-4 sm:p-6">
      <h3 class="text-heading-2 text-n-slate-12 mb-1 break-words">
        {{ t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.TITLE') }}
      </h3>
      <p class="text-body-main text-n-slate-11 mb-4">
        {{ t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.DESCRIPTION') }}
      </p>

      <!-- Stage filter (Fase 4.3) -->
      <div v-if="stages.length > 1" class="mb-4">
        <BulkStageFilter
          v-model="stageFilter"
          :items="items"
          :stages="stages"
        />
      </div>

      <!-- Item list with individual checkboxes -->
      <div
        class="max-h-48 overflow-y-auto mb-4 border border-n-weak rounded-xl"
      >
        <label
          class="flex min-h-11 items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-n-weak hover:bg-n-alpha-1 transition-colors rounded-t-xl"
        >
          <input
            type="checkbox"
            :checked="isAllSelected"
            :disabled="isSubmitting"
            class="size-4 flex-shrink-0 accent-n-brand"
            @change="toggleSelectAll"
          />
          <span class="text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.BULK_ACTIONS.SELECT_ALL') }}
          </span>
          <span class="ml-auto text-label-small text-n-slate-10">
            {{ selectedVisibleIds.length }}/{{ filteredItems.length }}
          </span>
        </label>
        <label
          v-for="(item, index) in filteredItems"
          :key="item.id"
          class="flex min-h-11 items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
          :class="[
            selectedIds.includes(item.id)
              ? 'bg-n-alpha-1'
              : 'hover:bg-n-alpha-1',
            index < filteredItems.length - 1 ? 'border-b border-n-weak' : '',
            index === filteredItems.length - 1 ? 'rounded-b-xl' : '',
          ]"
        >
          <input
            type="checkbox"
            :checked="selectedIds.includes(item.id)"
            :disabled="isSubmitting"
            class="size-4 flex-shrink-0 accent-n-brand"
            @change="toggleItem(item.id)"
          />
          <span class="text-label text-n-slate-12 truncate">
            {{
              item.item_details?.title ||
              item.title ||
              t('PIPELINE.ITEM_DETAILS.UNTITLED_ITEM')
            }}
          </span>
        </label>
      </div>

      <!-- Priority picker -->
      <div class="mb-6">
        <span
          id="bulk-priority-label"
          class="text-label-small font-medium text-n-slate-11 mb-2 block"
        >
          {{ t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.PRIORITY_LABEL') }}
        </span>
        <div
          role="group"
          aria-labelledby="bulk-priority-label"
          class="grid grid-cols-2 gap-2 sm:grid-cols-5"
        >
          <button
            v-for="opt in PRIORITY_OPTIONS"
            :key="opt"
            type="button"
            class="min-h-11 px-3 py-2 rounded-lg border text-label font-medium transition-[background-color,color,border-color,box-shadow]"
            :aria-pressed="selectedPriority === opt"
            :disabled="isSubmitting"
            :class="[
              selectedPriority === opt
                ? priorityClass(opt) + ' ring-2 ring-offset-1 ring-n-brand'
                : 'bg-n-alpha-1 text-n-slate-12 border-n-weak hover:bg-n-alpha-2',
            ]"
            @click="selectedPriority = opt"
          >
            {{ t(`PIPELINE.PRIORITY_LABELS.${opt.toUpperCase()}`) }}
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-between items-center gap-2">
        <span class="text-label-small text-n-slate-11">
          {{
            t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.AFFECT_COUNT', {
              count: selectedVisibleIds.length,
            })
          }}
        </span>
        <div class="flex gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="md"
            :disabled="isSubmitting"
            @click="$emit('close')"
          >
            {{ t('PIPELINE.BULK_ACTIONS.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="md"
            :disabled="!selectedVisibleIds.length || isSubmitting"
            :is-loading="isSubmitting"
            @click="handleConfirm"
          >
            {{
              t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.CONFIRM', {
                count: selectedVisibleIds.length,
              })
            }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
